import React, { useEffect, useRef, useState } from "react";
import {
  ConfirmationPopup,
  PaginationComponent,
  SearchInputComponent,
} from "../../../../../common/components";
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { Button } from "reactstrap";
import MUIBoxTable from "../../../../../common/components/mui-table/mui-box-table";
import CustomTooltip from "../../../../../common/components/custom-tooltip";
import { useTheme } from "@mui/material/styles";
import "./assign-partner.scss";
import { getTenets } from "../../../../../api/tenets";
import { assignCustomPlantoPartners } from "../../../../../api/crm-plans";
import PlanTypePopup from "./plantype-popup";

interface AssignPartnerProps {
  isOpen: boolean;
  setAssignPartnerPopup: (value: boolean) => void;
  selectedPlan: any;
  setIsLoading: (value: boolean) => void;
  fetchPlans: () => void;
  setAlertOpen: (value: boolean) => void;
  setAlertMessage: (message: string) => void;
  setAlertType: (type: "success" | "error" | "warning") => void;
}

const AssignPartner: React.FC<AssignPartnerProps> = ({
  isOpen,
  setAssignPartnerPopup,
  selectedPlan,
  setIsLoading,
  fetchPlans,
  setAlertOpen,
  setAlertMessage,
  setAlertType,
}) => {
  const [isPartnerAssignConfirmPopupOpen, setIsPartnerAssignConfirmPopupOpen] =
    useState(false);
  const [searchText, setSearchText] = useState<string>("");
  const [isResetSearch, setIsResetSearch] = useState(false);
  const [tenants, setTenants] = useState<any[]>([]);
  const [totalTenants, setTotalTenants] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isPlanTypePopupOpen, setIsPlanTypePopupOpen] = useState(false);
  const [selectedTenantDetails, setSelectedTenantDetails] = useState<any[]>([]);
  const [selectedTenants, setSelectedTenants] = useState<number[]>([]);
  const searchRef = useRef<any>(null);

  const pageSize = 15;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const headers = [
    {
      label: "",
      key: "actions",
      disableTooltip: true,
      render: (row: any) => {
        const isSamePlan = row.currentPlanName === selectedPlan.planName;
        return isSamePlan ? (
          <Tooltip title="Partner is already on this plan" placement="top">
            <span style={{ display: "inline-block" }}>
              <Checkbox disabled checked={false} />
            </span>
          </Tooltip>
        ) : row.hasPendingSubscription ? (
          <Tooltip
            title="A pending request already exists for this partner"
            placement="top"
          >
            <span style={{ display: "inline-block" }}>
              <Checkbox disabled checked={false} />
            </span>
          </Tooltip>
        ) : (
          <Checkbox
            checked={selectedTenants.includes(row.id)}
            onChange={(e) => {
              if (e.target.checked) {
                setSelectedTenants([...selectedTenants, row.id]);
              } else {
                setSelectedTenants(
                  selectedTenants.filter((id) => id !== row.id)
                );
              }
            }}
          />
        );
      },
    },
    { label: "Company Name", key: "companyName" },
    { label: "Email", key: "email" },
    { label: "Phone", key: "phoneNumber" },
    { label: "Current Plan", key: "currentPlanName" },
  ];

  const getPartnertList = async (page: number = 1, search: string = "") => {
    try {
      setIsLoading(true);
      const offset = (page - 1) * pageSize;
      const params: any = { limit: pageSize, offset, status: "ACTIVE" };
      if (search) params.searchText = search;

      const response = await getTenets(params);
      const formattedData = response?.data?.data?.data?.data?.map(
        (item: any) => ({
          companyName: item.company_name,
          email: item.email,
          phoneNumber: item.phone_number,
          currentPlanName:
            item?.activeTenantSubscriptions?.[0]?.subscriptionPlan?.plan_name,
          id: item.id,
          currentPlanPrice: item?.activeTenantSubscriptions?.[0]?.price,
          hasPendingSubscription: item?.hasPendingSubscription,
        })
      );

      setTenants(formattedData);
      setTotalTenants(response?.data?.data?.data?.totalRecords || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const buildPayload = () => {
    return {
      tenants: selectedTenantDetails.map((tenant) => {
        const planType = tenant.planType;
        const currentPrice = tenant.currentPlanPrice;
        const newPrice =
          planType === "monthly"
            ? tenant.selectedPlanMonthlyPrice
            : tenant.selectedPlanYearlyPrice;

        let changeType = "choosePlan";
        if (currentPrice && newPrice) {
          if (newPrice > currentPrice) changeType = "Upgrade";
          else if (newPrice < currentPrice) changeType = "Downgrade";
        }

        return {
          tenantId: tenant.tenantId,
          changeType,
          planCategory: "custom",
          planType,
          companyName: tenant.companyName,
        };
      }),
    };
  };

  const handleAssignPartnerAPI = async () => {
    setIsLoading(true);
    try {
      await assignCustomPlantoPartners(buildPayload(), selectedPlan.id);
      setAlertOpen(true);
      setAlertType("success");
      setAlertMessage(
        "Partner(s) assignment initiated. Complete payment if required."
      );

      await fetchPlans();
    } catch (error: any) {
      setAlertOpen(true);
      setAlertType("error");
      setAlertMessage(
        error?.response?.data?.message || "Failed to assign partner(s) to plan."
      );
    } finally {
      setIsPartnerAssignConfirmPopupOpen(false);
      setIsPlanTypePopupOpen(false);
      setAssignPartnerPopup(false);
      setIsLoading(false);
    }
  };

  const handleClose = () => setAssignPartnerPopup(false);

  const handlePageChange = (_event: any, value: number) =>
    setCurrentPage(value);

  const onSearch = (value: string) => {
    setCurrentPage(1);
    setSearchText(value);
  };

  const handleAssignPartner = () => {
    if (!selectedTenants.length) {
      alert("Please select at least one tenant.");
      return;
    }
    prepareTenantDetails();
  };

  const prepareTenantDetails = () => {
    const list = tenants
      .filter((t) => selectedTenants.includes(t.id))
      .map((t) => ({
        tenantId: t.id,
        companyName: t.companyName,
        currentPlanName: t.currentPlanName,
        currentPlanPrice: t.currentPlanPrice,
        selectedPlanName: selectedPlan.planName,
        selectedPlanMonthlyPrice: selectedPlan.monthlyPrice,
        selectedPlanYearlyPrice: selectedPlan.annualPrice,
      }));
    setSelectedTenantDetails(list);
    setIsPlanTypePopupOpen(true);
  };

  useEffect(() => {
    getPartnertList(currentPage, searchText);
  }, [searchText, currentPage]);

  return (
    <div className="assign-partner-dialog">
      <Dialog
        open={isOpen}
        slotProps={{
          paper: {
            sx: {
              width: isMobile ? "90%" : "1000px",
              maxWidth: "95vw",
              minHeight: "280px",
              borderBottomLeftRadius: "30px",
              borderBottomRightRadius: "30px",
              top: 0,
              margin: 0,
              position: "absolute",
              backgroundColor: "#f1f1f1",
            },
          },
        }}
        className="custom-plan-assign-partner-dialog"
      >
        <DialogTitle>
          Assign Partner
          {selectedPlan?.planName ? (
            <Box
              component="span"
              ml={1}
              sx={{ display: "inline-block", maxWidth: "60%" }}
            >
              <CustomTooltip
                title={selectedPlan.planName}
                placement="top-start"
              >
                <span
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "inline-block",
                    verticalAlign: "middle",
                  }}
                >
                  {selectedPlan.planName}
                </span>
              </CustomTooltip>
            </Box>
          ) : null}
        </DialogTitle>
        <DialogContent>
          <Box width="50%" mb={2} className="search-box">
            <SearchInputComponent
              placeholder="Search"
              onSearch={onSearch}
              isReset={isResetSearch}
              ref={searchRef}
            />
          </Box>
          <MUIBoxTable columns={headers} data={tenants} />
        </DialogContent>
        <DialogActions className="dialog-actions">
          {totalTenants > 0 && (
            <PaginationComponent
              currentPage={currentPage}
              pageSize={pageSize}
              totalRecords={totalTenants}
              onPageChange={handlePageChange}
            />
          )}
          <div className="button-group">
            <Button className="cancel-btn" onClick={handleClose}>
              Cancel
            </Button>
            <Button className="confirm-btn" onClick={handleAssignPartner}>
              Assign Partner
            </Button>
          </div>
        </DialogActions>
      </Dialog>

      <PlanTypePopup
        open={isPlanTypePopupOpen}
        tenants={selectedTenantDetails}
        onCancel={() => setIsPlanTypePopupOpen(false)}
        onSelect={(updated) => {
          setSelectedTenantDetails(updated);
          setIsPartnerAssignConfirmPopupOpen(true);
        }}
      />

      <ConfirmationPopup
        open={isPartnerAssignConfirmPopupOpen}
        showIcon={false}
        onCancel={() => setIsPartnerAssignConfirmPopupOpen(false)}
        onOkay={handleAssignPartnerAPI}
        title="Partner is currently on another plan"
        note="Do you still want to assign this partner to the new plan?"
        buttonText="Confirm"
      />
    </div>
  );
};

export default AssignPartner;
